import { useState, useEffect, useCallback } from "react";

export const formatTimecode = (date: Date, fps: number = 29.97): string => {
  const hours = date.getHours();
  const minutes = date.getMinutes();
  const seconds = date.getSeconds();
  const milliseconds = date.getMilliseconds();
  const frames = Math.floor((milliseconds / 1000) * fps);
  const pad = (n: number) => n.toString().padStart(2, "0");
  return `${pad(hours)}:${pad(minutes)}:${pad(seconds)};${pad(frames)}`;
};

interface UseTimecodeOptions {
  fps?: number;
  offsetMs?: number; // Default 3000ms as per brief
}

interface FrozenMark {
  timecode: string;
  at: Date;
}

export const useTimecode = ({ fps = 29.97, offsetMs = 3000 }: UseTimecodeOptions = {}) => {
  const [currentDisplayTC, setCurrentDisplayTC] = useState(() =>
    formatTimecode(new Date(Date.now() - offsetMs), fps),
  );
  // Keep the formatted label and its absolute instant together. Storing only
  // the label made the Logger persist Date.now() at submit time, losing the
  // moment that was actually frozen when the operator started the note.
  const [frozenMark, setFrozenMark] = useState<FrozenMark | null>(null);
  const frozenTC = frozenMark?.timecode ?? null;
  const frozenAt = frozenMark?.at ?? null;

  // Display format is HH:MM:SS — only need to update when the second ticks.
  // Bail out of setState if the formatted string hasn't changed to avoid spurious renders.
  useEffect(() => {
    const tick = () => {
      // The live display and frozen marks must use the same content clock.
      // Previously only freeze() applied the logger offset, so the large
      // running TC appeared ahead of both the video and the saved card.
      const formatted = formatTimecode(new Date(Date.now() - offsetMs), fps);
      setCurrentDisplayTC((prev) => (prev === formatted ? prev : formatted));
    };
    tick();
    const id = setInterval(tick, 250);
    return () => clearInterval(id);
  }, [fps, offsetMs]);

  // The Core Logic: Freeze
  const freeze = useCallback(() => {
    const now = new Date();
    // Apply offset (go back in time by offsetMs)
    const markTime = new Date(now.getTime() - offsetMs);
    const formattedMark = formatTimecode(markTime, fps);

    setFrozenMark({ timecode: formattedMark, at: markTime });

    return {
      systemTime: now,
      markTime,
      formattedMark,
    };
  }, [fps, offsetMs]);

  const unfreeze = useCallback(() => {
    setFrozenMark(null);
  }, []);

  return {
    currentDisplayTC,
    frozenTC,
    frozenAt,
    freeze,
    unfreeze,
  };
};
